<template>
  <TheAppLayout />
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { onMounted, watch } from 'vue'
import { useBroadcastChannel } from '@vueuse/core'
import { useQueryClient } from '@tanstack/vue-query'
import { useRoute, useRouter } from 'vue-router'
import useAuth from './modules/Auth/composables/useAuth'
import featureFlag from './config/featureFlag'
import useUserInfoQuery from './modules/Auth/requests/queries/useUserInfoQuery'
import useAuthStore from '@/modules/Auth/store'
import { useUserPreferencesQuery } from '@/modules/Core/requests/queries'
import TheAppLayout from '@/modules/Core/layouts/TheAppLayout.vue'
import useLocale from '@/modules/Core/composables/useLocale'
import useUserPreferencesStore from '@/modules/Core/store/userPreferences'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import { useItinerary } from '@/modules/Search/composables'
import {
  useCurrency,
  usePushNotification,
  useSentry,
  useUpdateServiceWorker,
} from '@/modules/Core/composables'
import { MAIN_CHANNEL } from '@/modules/Core/constants'
import type { SignupConfirmSuccessResponse } from '@/modules/Auth/types/api/response/auth'

useLocale()

const { registerServiceWorker } = useUpdateServiceWorker()

const { data: bcData } = useBroadcastChannel({ name: MAIN_CHANNEL })

onMounted(() => {
  if (!import.meta.env.DEV) {
    registerServiceWorker()
  }
})

useHead({
  title: 'City Of Dreams',
})

const { setFeatureFlagId } = useSentry()

// FIXME: SHOULD BE UPDATED WHEN DYNAMIC FEATUR FLAG IMPLEMENTED
setFeatureFlagId('')

// ITINERARY
const itinerarySummaryStore = useItinerarySummary()
const { fetchItinerary } = useItinerary()
const { data: itierariesData, isFetching: itierariesIsLoading } =
  fetchItinerary()

watch(itierariesData, () => {
  if (!itierariesData.value?.data) {
    return
  }

  itinerarySummaryStore.setItineraries(itierariesData.value.data)
})

watch(
  itierariesIsLoading,
  (value) => {
    itinerarySummaryStore.itinerariesIsLoading = value
  },
  {
    immediate: true,
  }
)

// CURRENCY
useCurrency()

// USER PREFERENCES
const authStore = useAuthStore()

const { data: userPreferencesData, refetch } = useUserPreferencesQuery({
  enabled: false,
})

const userPreferencesStore = useUserPreferencesStore()

function fetchUserPreferencesIfUserIsLoggedIn() {
  if (!authStore.loggedIn) {
    return
  }

  refetch()
}

onMounted(fetchUserPreferencesIfUserIsLoggedIn)

const { resetFcmNotification } = usePushNotification()

const queryClient = useQueryClient()

const auth0 = useAuth()

const route = useRoute()
const router = useRouter()

// FETCH USER INFO
const { refetch: refetchUserInfo } = useUserInfoQuery({
  enabled: false,
})

const { setUserContext } = useSentry()

async function fetchUserInfo() {
  const { data } = await refetchUserInfo()

  if (!data) {
    return
  }

  authStore.setUser(data.data)
  setUserContext(data.data)

  userPreferencesStore.setNotificationPreferences(
    data.data.preferences.notification
  )
}

watch(bcData, (data) => {
  try {
    const { action, payload } = data as {
      action: string
      payload: SignupConfirmSuccessResponse
    }
    if (action === 'logout') {
      resetFcmNotification()
      queryClient.clear()
      itinerarySummaryStore.$reset()

      if (featureFlag.authMode === 'legacy') {
        if (route.meta.requiresAuth) {
          router.replace({ name: 'Search' })
        }
      }

      if (featureFlag.authMode === 'auth0') {
        auth0.logout()
        localStorage.removeItem('id_token')
      }

      authStore.logout()
    } else if (action === 'login') {
      authStore.setTokens(payload)
      authStore.setStep('loggedIn')
      refetch()

      fetchUserInfo()
    }
  } catch (e) {
    console.warn(e)
  }
})

watch(userPreferencesData, () => {
  if (!userPreferencesData.value?.data) {
    return
  }

  userPreferencesStore.setNotificationPreferences(
    userPreferencesData.value?.data.notification
  )
})

// LANGUAGE
const { applyCurrentLanguage } = useLocale()
onMounted(applyCurrentLanguage)

async function setUser() {
  await auth0.getAccessTokenSilently()

  const email = auth0.idTokenClaims.value?.email ?? ''
  const givenName = auth0.idTokenClaims.value?.given_name ?? ''
  const familyName = auth0.idTokenClaims.value?.family_name ?? ''
  const id = auth0.idTokenClaims.value?.preferred_username ?? ''

  authStore.setUser({
    email,
    firstname: givenName,
    lastname: familyName,
    id,
  })
  authStore.setStep('loggedIn')
}

watch([auth0.isAuthenticated, auth0.user], async () => {
  if (featureFlag.authMode === 'legacy') {
    return
  }

  if (
    auth0.isAuthenticated.value &&
    auth0.user.value &&
    Object.keys(auth0.user.value).length
  ) {
    const token = localStorage.getItem('id_token')
    if (!token) {
      return
    }

    setUser()
  }
})
</script>
